/**
 * 锁屏 / 通知栏媒体控件适配（Media Session API）
 * 在 iOS、Android 系统播放小组件上展示歌曲名、艺术家与封面，并响应播放控制。
 */
import { watch } from 'vue';
import { usePlaybackStore } from '@store/playback';
import { getPlaybackStrategyManager } from '@services/PlaybackStrategyManager.js';
import CoverService from '@services/CoverService.js';
import * as tokenService from '@utils/tokenService';

const USE_SVG_ICON = 'USE_SVG_ICON';
const ALIYUN_COVER_PREFIX = 'aliyun-cover:';
let initialized = false;
let lastPositionUpdateAt = 0;
let metadataUpdateToken = 0;

function getOrigin() {
  if (typeof window === 'undefined') return '';
  return window.location.origin || '';
}

function getDefaultArtworkUrl() {
  return `${getOrigin()}/favicon.png`;
}

function buildCoverApiUrl(songId) {
  const token = tokenService.getAccessToken();
  if (!songId || !token) return getDefaultArtworkUrl();
  return `${getOrigin()}/api/music/${encodeURIComponent(songId)}/cover?token=${encodeURIComponent(token)}`;
}

function getSongIdentity(song) {
  if (!song) return '';
  const id = song.id ?? song.songId;
  return id == null ? '' : String(id);
}

/**
 * 同步兜底：先展示标题/作者，封面稍后异步补齐。
 */
function resolveArtworkUrlSync(song) {
  if (!song) return getDefaultArtworkUrl();

  const directHttps = song.coverDirectUrl;
  if (typeof directHttps === 'string' && directHttps.startsWith('https://')) {
    return directHttps;
  }

  const raw = song.coverUrl || song.cover || song._originalCoverUrl || '';
  const songId = song.id ?? song.songId;

  if (raw && raw !== USE_SVG_ICON) {
    if (raw.startsWith('https://')) {
      return raw;
    }
    if (raw.startsWith('http://')) {
      return raw;
    }
    if (raw.startsWith('blob:') || raw.startsWith(ALIYUN_COVER_PREFIX)) {
      return getDefaultArtworkUrl();
    }
    if (raw.startsWith('/')) {
      return `${getOrigin()}${raw}`;
    }
  }

  if (songId != null && tokenService.getAccessToken()) {
    return buildCoverApiUrl(songId);
  }

  return getDefaultArtworkUrl();
}

function buildArtworkEntries(artworkUrl) {
  if (!artworkUrl) return [];

  const type = artworkUrl.endsWith('.png')
    ? 'image/png'
    : artworkUrl.endsWith('.webp')
      ? 'image/webp'
      : 'image/jpeg';

  return [
    { src: artworkUrl, sizes: '96x96', type },
    { src: artworkUrl, sizes: '128x128', type },
    { src: artworkUrl, sizes: '256x256', type },
    { src: artworkUrl, sizes: '512x512', type }
  ];
}

function getSongTitle(song) {
  return (
    song?.title ||
    song?.name ||
    song?.fileName ||
    '未知歌曲'
  );
}

function getSongArtist(song) {
  return (
    song?.artist ||
    song?.author ||
    song?.singer ||
    '未知艺术家'
  );
}

function applyMetadata(song, artworkUrl) {
  if (!navigator.mediaSession || !song) return;

  navigator.mediaSession.metadata = new MediaMetadata({
    title: getSongTitle(song),
    artist: getSongArtist(song),
    album: song.album || song.albumName || '',
    artwork: buildArtworkEntries(artworkUrl)
  });
}

async function updateMetadata(song) {
  if (!navigator.mediaSession) return;

  if (!song) {
    navigator.mediaSession.metadata = null;
    return;
  }

  const requestToken = ++metadataUpdateToken;
  const songIdentity = getSongIdentity(song);

  applyMetadata(song, resolveArtworkUrlSync(song));

  try {
    const resolvedUrl = await CoverService.resolveMediaSessionArtworkUrl(song);
    if (requestToken !== metadataUpdateToken) return;

    const playbackStore = usePlaybackStore();
    if (getSongIdentity(playbackStore.currentSong) !== songIdentity) return;

    if (resolvedUrl) {
      song._mediaSessionArtworkUrl = resolvedUrl;
      applyMetadata(song, resolvedUrl);
      return;
    }

    if (song._mediaSessionArtworkUrl) {
      applyMetadata(song, song._mediaSessionArtworkUrl);
    }
  } catch (error) {
    console.warn('[MediaSession] 封面解析失败，使用兜底图:', error);
  }
}

function updatePlaybackState(isPlaying) {
  if (!navigator.mediaSession) return;
  navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
}

function updatePositionState(playbackStore) {
  if (!navigator.mediaSession?.setPositionState) return;

  const duration = Number(playbackStore.duration);
  const position = Number(playbackStore.currentTime);
  if (!Number.isFinite(duration) || duration <= 0) return;

  const now = Date.now();
  if (now - lastPositionUpdateAt < 500) return;
  lastPositionUpdateAt = now;

  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: 1,
      position: Math.max(0, Math.min(position, duration))
    });
  } catch (error) {
    console.debug('[MediaSession] setPositionState 失败:', error);
  }
}

function setActionHandler(action, handler) {
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch (error) {
    console.debug(`[MediaSession] 不支持操作: ${action}`, error);
  }
}

/**
 * iOS Safari 上滑回桌面时，会在 visibilitychange(hidden) 前后误发 Media Session pause。
 * 后台停留超过约 1 秒后仍可能再发 pause（RPT-49668c776e80），不能只用短窗口。
 * 回前台后点播放，也可能收到延迟 pause，导致播/停循环。
 * 策略：页面 hidden 期间一律忽略 MediaSession pause；回前台/用户起播后短宽限内也忽略。
 */
let lastBecameVisibleAt = 0;
let lastUserPlayIntentAt = 0;
let backgroundPauseTimer = 0;
const IOS_FOREGROUND_PAUSE_GRACE_MS = 2000;
const IOS_BACKGROUND_PAUSE_DELAY_MS = 80;

function markDocumentVisibleIfNeeded() {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible') {
    lastBecameVisibleAt = Date.now();
  }
}

function clearBackgroundPauseTimer() {
  if (backgroundPauseTimer) {
    window.clearTimeout(backgroundPauseTimer);
    backgroundPauseTimer = 0;
  }
}

function isLikelyIosSpuriousPause() {
  if (typeof document === 'undefined') return false;
  // 在后台：上滑桌面 / 系统挂起触发的 pause 都忽略，保持 Store 播放意图
  if (document.hidden) return true;
  const now = Date.now();
  // 刚回到前台：系统常补发一次延迟 pause
  if (lastBecameVisibleAt && now - lastBecameVisibleAt < IOS_FOREGROUND_PAUSE_GRACE_MS) {
    return true;
  }
  // 用户刚点播放：避免 MediaSession 状态同步立刻又 pause
  if (lastUserPlayIntentAt && now - lastUserPlayIntentAt < IOS_FOREGROUND_PAUSE_GRACE_MS) {
    return true;
  }
  return false;
}

function releaseWebAudioTapForBackground() {
  try {
    const manager = getPlaybackStrategyManager();
    const audioController = manager?.currentStrategy?.audioController;
    if (audioController?.releaseWebAudioTapKeepingPlayback) {
      audioController.releaseWebAudioTapKeepingPlayback();
    }
  } catch (error) {
    console.warn('[MediaSession] 切后台释放 Web Audio tap 失败:', error);
  }
}

function keepPlayingInBackground(playbackStore) {
  console.log('[MediaSession] 忽略 iOS 切后台触发的 pause，保持后台播放');
  updatePlaybackState(true);
  if (!playbackStore.isPlaying || !playbackStore.currentSong) return;
  try {
    const manager = getPlaybackStrategyManager();
    if (manager?.currentStrategy) {
      manager.currentStrategy._userInitiatedPause = false;
    }
    if (manager?.isPlaying?.()) return;
    manager?.play?.().catch((error) => {
      console.warn('[MediaSession] 切后台后恢复播放失败:', error);
    });
  } catch (error) {
    console.warn('[MediaSession] 切后台后恢复播放失败:', error);
  }
}

function bindActionHandlers(playbackStore) {
  setActionHandler('play', () => {
    if (playbackStore.currentSong) {
      lastUserPlayIntentAt = Date.now();
      clearBackgroundPauseTimer();
      try {
        const manager = getPlaybackStrategyManager();
        if (manager?.currentStrategy) {
          manager.currentStrategy._userInitiatedPause = false;
        }
      } catch {
        /* ignore */
      }
      playbackStore.setPlaying(true);
    }
  });

  const handlePauseOrStop = (actionName) => {
    clearBackgroundPauseTimer();
    backgroundPauseTimer = window.setTimeout(() => {
      backgroundPauseTimer = 0;
      if (isLikelyIosSpuriousPause()) {
        keepPlayingInBackground(playbackStore);
        return;
      }
      console.log(`[MediaSession] ${actionName}，暂停播放`);
      playbackStore.setPlaying(false);
    }, IOS_BACKGROUND_PAUSE_DELAY_MS);
  };

  setActionHandler('pause', () => handlePauseOrStop('pause'));
  setActionHandler('stop', () => handlePauseOrStop('stop'));

  setActionHandler('previoustrack', () => {
    if (!playbackStore.currentSong) return;
    getPlaybackStrategyManager()
      .prevSong()
      .catch((error) => console.error('[MediaSession] 上一首失败:', error));
  });

  setActionHandler('nexttrack', () => {
    if (!playbackStore.currentSong) return;
    getPlaybackStrategyManager()
      .nextSong({ forceAutoPlay: true, reason: 'media-session' })
      .catch((error) => console.error('[MediaSession] 下一首失败:', error));
  });
}

export function initMediaSession() {
  if (initialized) return;
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
    console.log('[MediaSession] 当前环境不支持 Media Session API');
    return;
  }

  initialized = true;
  const playbackStore = usePlaybackStore();

  markDocumentVisibleIfNeeded();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (!playbackStore.isPlaying) return;
      // 先把出声从 AudioContext 解绑回原生 audio，再尝试继续播
      releaseWebAudioTapForBackground();
      window.setTimeout(() => {
        if (!document.hidden || !playbackStore.isPlaying) return;
        keepPlayingInBackground(playbackStore);
      }, IOS_BACKGROUND_PAUSE_DELAY_MS);
      return;
    }

    markDocumentVisibleIfNeeded();
    clearBackgroundPauseTimer();
    // 回前台：若 Store 仍为播放而音频被系统掐了，拉回播放（避免点播放后立刻被延迟 pause 打断）
    if (playbackStore.isPlaying && playbackStore.currentSong) {
      window.setTimeout(() => {
        if (document.hidden || !playbackStore.isPlaying) return;
        keepPlayingInBackground(playbackStore);
      }, IOS_BACKGROUND_PAUSE_DELAY_MS);
    }
  });

  bindActionHandlers(playbackStore);

  watch(
    () => playbackStore.currentSong,
    (song) => {
      updateMetadata(song);
    },
    { immediate: true, deep: true }
  );

  watch(
    () => playbackStore.isPlaying,
    (isPlaying) => {
      if (isPlaying) {
        lastUserPlayIntentAt = Date.now();
        clearBackgroundPauseTimer();
      }
      updatePlaybackState(isPlaying);
    },
    { immediate: true }
  );

  watch(
    () => [playbackStore.currentTime, playbackStore.duration],
    () => updatePositionState(playbackStore)
  );

  console.log('[MediaSession] 锁屏/通知栏媒体控件已初始化');
}

export function refreshMediaSessionMetadata(song) {
  updateMetadata(song || usePlaybackStore().currentSong);
}
